import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { LogoutOutlined } from '@ant-design/icons';
import { Collapse } from 'antd';

import { useAuthorization } from 'entities/user/hooks/useAuthorization';
import { UserDisplayM } from 'entities/user/ui/CurrentUserM/UserDisplayM';
import { updateMyPage } from 'shared/lib/myPageSlice';
import { useAppDispatch } from 'shared/lib/reduxHooks';
import { LanguagePicker } from 'shared/ui/LanguagePicker/LanguagePicker';
import {
	ButtonStyled,
	ButtonSubmenuStyled,
	CollapseStyled,
	ContainerStyled,
	DividerStyled,
	MobileMenuBackgroundStyled,
	MobileMenuContainerStyled,
	MobileMenuListItemStyled,
	MobileMenuListStyled,
} from './MobileMenu.styles';

interface Props {
	toggleVisible: () => void;
}

export const MobileMenu: FC<Props> = ({ toggleVisible }) => {
	const { t } = useTranslation();
	const authorization = useAuthorization();
	const canPurchaseApps = authorization?.canPurchaseApps;
	const dispatch = useAppDispatch();
	const currentPage = window.location.pathname + window.location.search;

	const handleClick = (key: string): void => {
		localStorage.setItem(`my-page`, key);
		dispatch(
			updateMyPage({
				myPage: key,
			}),
		);
		toggleVisible();
	};

	return (
		<>
			<MobileMenuBackgroundStyled onClick={toggleVisible} />
			<MobileMenuContainerStyled>
				<DividerStyled />
				<ContainerStyled>
					<UserDisplayM />
				</ContainerStyled>
				<DividerStyled />
				<ContainerStyled>
					<MobileMenuListStyled>
						<Link to='/'>
							<MobileMenuListItemStyled>
								<ButtonStyled type='link' onClick={toggleVisible} active={currentPage === `/`}>
									{t(`Marketplace_All_Apps`)}
								</ButtonStyled>
							</MobileMenuListItemStyled>
						</Link>
						<CollapseStyled
							ghost
							className='mobile-menu-collapse'
							expandIconPosition='end'
							defaultActiveKey={[window.location.pathname === `/my` ? `app-management` : ``]}
						>
							<Collapse.Panel key='app-management' header={t(`Marketplace_My_Apps_And_Subscriptions`)}>
								<MobileMenuListStyled>
									<Link to='/my?tab=apps'>
										<ButtonSubmenuStyled
											type='link'
											onClick={() => handleClick(`apps`)}
											active={currentPage === `/my?tab=apps`}
										>
											{t(`Marketplace_My_Apps`)}
										</ButtonSubmenuStyled>
									</Link>
									<Link to='/my?tab=subscriptions'>
										<ButtonSubmenuStyled
											type='link'
											onClick={() => handleClick(`subscriptions`)}
											active={currentPage === `/my?tab=subscriptions`}
										>
											{t(`Marketplace_Subscription_Management`)}
										</ButtonSubmenuStyled>
									</Link>
									{canPurchaseApps && (
										<Link to='/my?tab=invoices'>
											<ButtonSubmenuStyled
												type='link'
												onClick={() => handleClick(`invoices`)}
												active={currentPage === `/my?tab=invoices`}
											>
												{t(`Marketplace_My_Bills`)}
											</ButtonSubmenuStyled>
										</Link>
									)}
								</MobileMenuListStyled>
							</Collapse.Panel>
						</CollapseStyled>
					</MobileMenuListStyled>
				</ContainerStyled>
				<DividerStyled style={{ marginTop: `12px` }} />
				<ContainerStyled>
					<a href={`/privateapi/auth/logout?returnUrl=${window.location.origin}`}>
						<MobileMenuListItemStyled>
							<ButtonStyled type='link' danger icon={<LogoutOutlined />} active={false}>
								{t(`Marketplace_Log_Out`)}
							</ButtonStyled>
						</MobileMenuListItemStyled>
					</a>
				</ContainerStyled>
				<DividerStyled />
				<ContainerStyled>
					<LanguagePicker full mobile />
				</ContainerStyled>
			</MobileMenuContainerStyled>
		</>
	);
};
